// Public bylines and verified founder profiles.

import type { useTranslations } from 'next-intl'

import type { Messages } from '@/lib/i18n/messages'

/** `useTranslations('blogPage')`, or `getTranslations('blogPage')` on the server. */
type BlogTranslator = ReturnType<typeof useTranslations<'blogPage'>>

export type Author = {
  id: string
  name: string
  role: string
  /** Its `blogPage.authors` entry: the role (and the team's name) in the reader's language. */
  messageKey: keyof Messages['blogPage']['authors']
  avatar: string
  /** Public profile URLs for Person JSON-LD `sameAs`. Omit when unverified. */
  sameAs?: Array<string>
}

export const authors: Record<string, Author> = {
  'brew-team': {
    id: 'brew-team',
    name: 'Brew Team',
    role: 'Brew editorial team',
    messageKey: 'brewTeam',
    avatar: '/logo/circle_orange.png',
  },
  'philip-sorensen': {
    id: 'philip-sorensen',
    name: 'Philip Sørensen',
    role: 'Co-founder & CEO, Brew',
    messageKey: 'philipSorensen',
    avatar: '/people/philip_sorensen.png',
    sameAs: ['https://www.linkedin.com/in/philipsorensen'],
  },
  'thomas-park': {
    id: 'thomas-park',
    name: 'Thomas Park',
    role: 'Co-founder & CTO, Brew',
    messageKey: 'thomasPark',
    avatar: '/people/thomas_park.jpeg',
    sameAs: ['https://www.linkedin.com/in/thomasjunseopark'],
  },
}

// Helper function to get author by name (case-insensitive, handles spaces)
export function getAuthorByName(name: string | undefined): Author | undefined {
  if (!name) {
    return
  }

  // Convert name to id format (lowercase, spaces to hyphens)
  const nameId = name.toLowerCase().replace(/\s+/g, '-')

  // Try direct lookup first
  if (authors[nameId]) {
    return authors[nameId]
  }

  // Try matching by name
  return Object.values(authors).find(
    (author) => author.name.toLowerCase() === name.toLowerCase()
  )
}

/**
 * An author's byline in the reader's language: the team's name and every
 * role are translated, a person's name stays as written. Structured data
 * keeps the English `name` and `role`, one entity across languages.
 */
export function authorByline(
  t: BlogTranslator,
  author: Author
): { name: string; role: string } {
  return {
    name:
      author.messageKey === 'brewTeam'
        ? t('authors.brewTeam.name')
        : author.name,
    role: t(`authors.${author.messageKey}.role`),
  }
}
